import React from 'react'
import {Button, NakedButton} from '../../../components/Button'
import {Container} from '../../../components/Container'
import {LoadingError} from '../../../components/LoadingFork'
import {H3, HR} from '../../../components/Typography'
import {Input} from '../../../components/Input'
import {SelectInput} from '../../../components/SelectInput'
import {Table, TBody} from '../../../components/Table'
import {css} from 'glamor'
import {useFormState} from 'react-use-form-state'
import {useMutation} from "@apollo/client"
import {createLotMutation, updateLotMutation} from './queries'
import * as theme from "../../../theme";
import {PERMISSIONS} from "../../../lib/authManager";
import {PermissionOnly} from "../../../components/AdminOnly";

const CF_CSS = css({
    display: 'flex',
    alignItems: 'center',
    borderWidth: 2,
    borderStyle: 'solid',
    borderColor: '#FAFAFA',
    borderRadius: 6,
    fontSize: 22,
    fontWeight: 'bold',
    height: 60,
    paddingLeft: 16,
    paddingRight: 16,
    marginBottom: 16,
})

const ALIGN_LEFT = css({textAlign: 'left !important'})
const ALIGN_RIGHT = css({textAlign: 'right !important'})

export const DEFAULT_AVG_PICKUP_MINUTES = 5

export function avgPickupMinutesOrDefault(value) {
    if (value === '' || value === null || value === undefined) {
        return DEFAULT_AVG_PICKUP_MINUTES
    }

    return Number(value)
}

const LOT_TYPE_OPTIONS = [
    {value: 'ONSITE', label: 'On-site'},
    {value: 'OFFSITE', label: 'Off-site'},
]

function optionalNumber(value) {
    if (value === '' || value === null || value === undefined) return null

    return Number(value)
}

function optionalString(value) {
    if (value === '' || value === null || value === undefined) return null

    return value
}

export function TextInputRow({name, ...props}) {
    return <tr>
        <td>
            <span className={ALIGN_LEFT}>{name}</span>
        </td>
        <td className={ALIGN_RIGHT}>
            <Input
                {...props}
            />
        </td>
    </tr>
}

function SelectInputRow({name, ...props}) {
    return <tr>
        <td>
            <span className={ALIGN_LEFT}>{name}</span>
        </td>
        <td className={ALIGN_RIGHT}>
            <SelectInput
                {...props}
            />
        </td>
    </tr>
}

function TextAreaRow({name, ...props}) {
    return <tr>
        <td>
            <span className={ALIGN_LEFT}>{name}</span>
        </td>
        <td className={ALIGN_RIGHT}>
            <textarea
                className={css(
                    {
                        display: 'block',
                        width: '100%',
                        background: theme.white,
                        borderWidth: '2px',
                        borderStyle: 'solid',
                        borderColor: '#EAEDEF',
                        borderRadius: '6px',
                        fontSize: '18px',
                        transition: theme.transitionSnappy,
                        ':focus': {
                            outline: 'none',
                            borderColor: theme.blue,
                        },
                        ':disabled': {
                            opacity: 0.5,
                        },
                        '::placeholder': {
                            color: theme.gray20,
                        },
                    },
                )}

                {...props}
            />
        </td>
    </tr>
}

export default function DetailsForm({
    installation,
    lot,
    onSubmit,
    onCancel
}) {
    const [{
        errors,
        values
    }, {text, checkbox, select, raw, number}] = useFormState({
        name: lot ? lot.name : '',
        description: lot ? lot.description : '',
        type: lot?.type || 'ONSITE',
        capacity: lot ? lot.capacity : null,
        address: lot?.address || '',
        walkingDistanceMinutes: lot ? lot.walkingDistanceMinutes : null,
        avgPickupMinutes: lot ? lot.avgPickupMinutes : DEFAULT_AVG_PICKUP_MINUTES,
    })

    const [createLot, {error: submitError}] = useMutation(createLotMutation)
    const [updateLot, {error: submitErrorUpdate}] = useMutation(updateLotMutation)


    async function _onSubmit() {
        const lotInput = {
            ...values,
            capacity: optionalNumber(values.capacity),
            address: optionalString(values.address),
            walkingDistanceMinutes: optionalNumber(values.walkingDistanceMinutes),
            avgPickupMinutes: avgPickupMinutesOrDefault(values.avgPickupMinutes),
        }

        if (lot) {
            const {data} = await updateLot({
                variables: {
                    id: lot.id,
                    ...lotInput,
                }
            })
            return onSubmit(data.updateLot)
        } else {
            const {data} = await createLot({
                variables: {
                    installationId: installation.id,
                    ...lotInput,
                }
            })
            return onSubmit(data.createLot)

        }
    }

    const isCreating = !lot
    const hasErrors = Object.values(errors).some(x => x)
    const canSubmit = !hasErrors


    return <div>
        {submitError && <LoadingError error={submitError}/>}
        {submitErrorUpdate && <LoadingError error={submitErrorUpdate}/>}
        {hasErrors && <LoadingError error={Object.values(errors)[0]}/>}

        {isCreating ? <H3>Parking Lot Details</H3> : null}

        <HR spaceAbove='medium' spaceBelow='medium'/>

        <Table>
            <TBody>


                <TextInputRow key={"name"}
                              {...text('name')}
                              rows={1}
                              name={"Name"}/>
                <TextAreaRow
                    {...text('description')}
                    rows="3"
                    cols="30"
                    key={"description"}
                    name={"Description"}
                />
                <SelectInputRow
                    key={"type"}
                    {...select('type')}
                    name={"Type"}
                    options={LOT_TYPE_OPTIONS}
                />
                <TextInputRow key={"capacity"}
                              {...number('capacity')}
                              rows={1}
                              name={"Capacity"}/>
                <TextInputRow key={"address"}
                              {...text('address')}
                              rows={1}
                              name={"Address"}/>
                <TextInputRow key={"walkingDistanceMinutes"}
                              {...number('walkingDistanceMinutes')}
                              rows={1}
                              name={"Walking distance, minutes"}/>
                <TextInputRow key={"avgPickupMinutes"}
                              {...number('avgPickupMinutes')}
                              rows={1}
                              name={"Average runner pickup time, minutes"}/>
            </TBody>
        </Table>


        <Container>
            <PermissionOnly permissions={PERMISSIONS.PARKING_LOTS_MANAGE}>
                <Button block type='submit' disabled={!canSubmit} onClick={_onSubmit}>
                    {isCreating ? 'Create' : 'Update card'}
                </Button>
                {' '}
            </PermissionOnly>
            <NakedButton block flavour='gray' onClick={onCancel}>Cancel</NakedButton>
        </Container>
    </div>
}
